/**
 * CharactersProvider
 * Провайдер для работы с персонажами книги
 */

import { createContext, useEffect, useState, ReactNode, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { Character, CreateCharacterInput, UpdateCharacterInput, CharacterError } from '@/types/character';
import { useBooks } from '@/books/useBooks';

interface CharactersContextType {
  characters: Character[];
  currentCharacter: Character | null;
  charactersLoading: boolean;
  charactersError: CharacterError | null;
  createCharacter: (input: CreateCharacterInput) => Promise<Character | null>;
  selectCharacter: (characterId: string) => void;
  saveCharacter: (characterId: string, updates: UpdateCharacterInput) => Promise<void>;
  deleteCharacter: (characterId: string) => Promise<void>;
  loadCharacters: () => Promise<void>;
}

export const CharactersContext = createContext<CharactersContextType | undefined>(undefined);

interface CharactersProviderProps {
  children: ReactNode;
}

export function CharactersProvider({ children }: CharactersProviderProps) {
  const { currentBook } = useBooks();
  const [characters, setCharacters] = useState<Character[]>([]);
  const [currentCharacter, setCurrentCharacter] = useState<Character | null>(null);
  const [charactersLoading, setCharactersLoading] = useState(false);
  const [charactersError, setCharactersError] = useState<CharacterError | null>(null);
  const pendingCharacterUpdatesRef = useRef<Map<string, UpdateCharacterInput>>(new Map());
  const characterSaveInFlightRef = useRef<Set<string>>(new Set());
  const lastSavedCharacterPayloadRef = useRef<Map<string, string>>(new Map());

  const applyCharacterUpdates = useCallback((character: Character, updates: UpdateCharacterInput): Character => ({
    ...character,
    ...updates,
    role: updates.role !== undefined ? updates.role : character.role,
    description: updates.description !== undefined ? updates.description : character.description,
    appearance: updates.appearance !== undefined ? updates.appearance : character.appearance,
    personality: updates.personality !== undefined ? updates.personality : character.personality,
    goals: updates.goals !== undefined ? updates.goals : character.goals,
    conflicts: updates.conflicts !== undefined ? updates.conflicts : character.conflicts,
    notes: updates.notes !== undefined ? updates.notes : character.notes,
    character_summary: updates.character_summary !== undefined ? updates.character_summary : character.character_summary,
    updated_at: new Date().toISOString(),
  }), []);

  // Дебаунс-сохранение персонажа в Supabase: стейт-машина с in-flight guard и хвостовой
  // дозаписью через самоссылку. deps=[] корректны — тело трогает только refs и стабильные
  // setState; самоссылка не даёт React Compiler сохранить ручную мемоизацию, поэтому
  // preserve-manual-memoization подавлен точечно (state-machine sync).
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const flushCharacterSave = useCallback(async (characterId: string) => {
    if (characterSaveInFlightRef.current.has(characterId)) {
      return;
    }

    const updates = pendingCharacterUpdatesRef.current.get(characterId);
    if (!updates || Object.keys(updates).length === 0) {
      return;
    }

    const payloadKey = JSON.stringify(updates);
    if (lastSavedCharacterPayloadRef.current.get(characterId) === payloadKey) {
      pendingCharacterUpdatesRef.current.delete(characterId);
      return;
    }

    characterSaveInFlightRef.current.add(characterId);

    try {
      const { data, error } = await supabase
        .from('characters')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', characterId)
        .select()
        .single();

      if (error) {
        console.error('Character save error:', error);
        setCharactersError({ message: 'Не удалось сохранить персонажа', code: error.code });
        return;
      }

      if (JSON.stringify(pendingCharacterUpdatesRef.current.get(characterId) ?? {}) === payloadKey) {
        pendingCharacterUpdatesRef.current.delete(characterId);
        lastSavedCharacterPayloadRef.current.set(characterId, payloadKey);
        setCharacters(prev => prev.map(c => c.id === characterId ? data : c));
        setCurrentCharacter(prev => prev?.id === characterId ? data : prev);
      }
    } catch (err) {
      console.error('Character save exception:', err);
      setCharactersError({ message: 'Произошла ошибка при сохранении персонажа' });
    } finally {
      characterSaveInFlightRef.current.delete(characterId);

      if (pendingCharacterUpdatesRef.current.has(characterId)) {
        void flushCharacterSave(characterId);
      }
    }
  }, []);

  // Загрузка персонажей текущей книги
  const loadCharacters = useCallback(async () => {
    if (!currentBook) {
      setCharacters([]);
      setCurrentCharacter(null);
      return;
    }

    setCharactersLoading(true);
    setCharactersError(null);

    try {
      const { data, error } = await supabase
        .from('characters')
        .select('*')
        .eq('book_id', currentBook.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Characters load error:', error);
        setCharactersError({ message: 'Не удалось загрузить персонажей', code: error.code });
        setCharacters([]);
      } else {
        setCharacters(data || []);
      }
    } catch (err) {
      console.error('Characters load exception:', err);
      setCharactersError({ message: 'Произошла ошибка при загрузке персонажей' });
      setCharacters([]);
    } finally {
      setCharactersLoading(false);
    }
  }, [currentBook]);

  // Создание нового персонажа
  const createCharacter = useCallback(async (input: CreateCharacterInput): Promise<Character | null> => {
    if (!currentBook) {
      setCharactersError({ message: 'Книга не выбрана' });
      return null;
    }

    setCharactersError(null);

    try {
      const newCharacter = {
        book_id: currentBook.id,
        name: input.name,
        role: input.role || null,
        description: input.description || null,
        appearance: null,
        personality: null,
        goals: null,
        conflicts: null,
        notes: null,
        character_summary: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('characters')
        .insert([newCharacter])
        .select()
        .single();

      if (error) {
        console.error('Character create error:', error);
        setCharactersError({ message: 'Не удалось создать персонажа', code: error.code });
        return null;
      }

      setCharacters(prev => [...prev, data]);
      setCurrentCharacter(data);
      
      return data;
    } catch (err) {
      console.error('Character create exception:', err);
      setCharactersError({ message: 'Произошла ошибка при создании персонажа' });
      return null;
    }
  }, [currentBook]);

  // Выбор персонажа
  const selectCharacter = useCallback((characterId: string) => {
    const character = characters.find(c => c.id === characterId);
    if (character) {
      setCurrentCharacter(character);
    }
  }, [characters]);

  // Сохранение изменений персонажа
  const saveCharacter = useCallback(async (characterId: string, updates: UpdateCharacterInput) => {
    const currentPendingUpdates = pendingCharacterUpdatesRef.current.get(characterId) ?? {};
    const nextUpdates = { ...currentPendingUpdates, ...updates };
    const payloadKey = JSON.stringify(nextUpdates);

    if (lastSavedCharacterPayloadRef.current.get(characterId) === payloadKey) {
      return;
    }

    pendingCharacterUpdatesRef.current.set(characterId, nextUpdates);
    setCharacters(prev => prev.map(c => c.id === characterId ? applyCharacterUpdates(c, updates) : c));
    setCurrentCharacter(prev => prev?.id === characterId ? applyCharacterUpdates(prev, updates) : prev);

    if (!characterSaveInFlightRef.current.has(characterId)) {
      void flushCharacterSave(characterId);
    }
  }, [applyCharacterUpdates, flushCharacterSave]);

  // Удаление персонажа
  const deleteCharacter = useCallback(async (characterId: string) => {
    try {
      const { error } = await supabase
        .from('characters')
        .delete()
        .eq('id', characterId);

      if (error) {
        console.error('Character delete error:', error);
        setCharactersError({ message: 'Не удалось удалить персонажа', code: error.code });
        return;
      }

      setCharacters(prev => prev.filter(c => c.id !== characterId));
      
      if (currentCharacter?.id === characterId) {
        setCurrentCharacter(null);
      }
    } catch (err) {
      console.error('Character delete exception:', err);
      setCharactersError({ message: 'Произошла ошибка при удалении персонажа' });
    }
  }, [currentCharacter]);

  // Загружаем персонажей при смене книги
  const currentBookId = currentBook?.id;
  useEffect(() => {
    if (currentBookId) {
      // loadCharacters — async-загрузка из Supabase; синхронный setCharactersLoading(true)
      // внутри неё и есть намеренный эффект, подавляем точечно.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadCharacters();
    } else {
      setCharacters([]);
      setCurrentCharacter(null);
    }
  }, [currentBookId, loadCharacters]);

  useEffect(() => {
    const pendingUpdates = pendingCharacterUpdatesRef.current;
    const saveInFlight = characterSaveInFlightRef.current;
    const lastSavedPayload = lastSavedCharacterPayloadRef.current;
    return () => {
      pendingUpdates.clear();
      saveInFlight.clear();
      lastSavedPayload.clear();
    };
  }, []);

  const value = useMemo(
    () => ({
      characters,
      currentCharacter,
      charactersLoading,
      charactersError,
      createCharacter,
      selectCharacter,
      saveCharacter,
      deleteCharacter,
      loadCharacters,
    }),
    [
      characters,
      currentCharacter,
      charactersLoading,
      charactersError,
      createCharacter,
      selectCharacter,
      saveCharacter,
      deleteCharacter,
      loadCharacters,
    ]
  );

  return <CharactersContext.Provider value={value}>{children}</CharactersContext.Provider>;
}
